import { jsPDF } from 'jspdf';
// v3 se instala como plugin sobre jsPDF: se importa por su efecto y se llama `doc.autoTable`,
// igual que en el resto de los PDF del proyecto.
import 'jspdf-autotable';
import QRCode from 'qrcode';

/**
 * Representación Impresa (RI) del e-CF — Paso 5 de la certificación de la DGII.
 *
 * Genera el PDF conforme a la sección 18 del Informe Técnico e-CF: orden y distribución de la
 * información, código QR de al menos 22×22 mm a 2 cm o más del borde, código de seguridad y fecha
 * de firma. Son dos formatos, siguiendo los modelos ilustrativos de la DGII:
 *
 *   • Fiscales (31, 33, 34, 41, 43-47): página completa tamaño carta.
 *   • Consumo (32): ticket de papel continuo.
 *
 * Se dibuja en el navegador y no en el servidor porque aquí ya viven jsPDF y la librería de QR, y
 * el PDF no necesita nada que solo tenga el backend: los datos llegan ya resueltos de `/cert/ri`.
 */

export interface RiItem {
  linea: number;
  exento: boolean;
  nombre: string;
  unidad: string;
  cantidad: number;
  precio: number;
  itbis: number;
  descuento: number;
  valor: number;
}

export interface RiData {
  encf: string;
  docType: string;
  tipoLabel: string;
  sendVia: 'ecf' | 'rfce';
  fechaVencimiento: string;
  ncfModificado: string;
  fechaNcfModificado: string;
  codigoModificacion: string;
  codigoModificacionLabel: string;
  emisor: {
    rnc: string;
    razonSocial: string;
    nombreComercial: string;
    sucursal: string;
    direccion: string;
    municipio: string;
    provincia: string;
    fechaEmision: string;
  };
  comprador: { rnc: string; razonSocial: string };
  items: RiItem[];
  totales: {
    subtotalGravado: number;
    subtotalExento: number;
    totalItbis: number;
    totalIsc: number;
    total: number;
  };
  fechaFirma: string;
  securityCode: string;
  qrUrl: string;
}

/** Lo que el plugin de autotable añade a jsPDF en tiempo de ejecución. */
type DocConTabla = jsPDF & {
  autoTable: (opts: Record<string, unknown>) => void;
  lastAutoTable?: { finalY: number };
};

const money = (v: number) =>
  v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const qty = (v: number) => v.toLocaleString('en-US', { maximumFractionDigits: 4 });

function qrPng(url: string, px: number): Promise<string> {
  return QRCode.toDataURL(url, { margin: 1, width: px, errorCorrectionLevel: 'M' });
}

/** La DGII exige 22 mm como mínimo y 2 cm de separación al borde; se deja algo de holgura. */
const QR_MM = 25;
const QR_MARGIN_MM = 20;

const NAVY: [number, number, number] = [31, 73, 125];

// ─── Página completa (comprobantes con valor fiscal) ─────────────────────────

async function buildFullPage(d: RiData): Promise<jsPDF> {
  const doc = new jsPDF({ unit: 'mm', format: 'letter' }) as DocConTabla;
  const W = doc.internal.pageSize.getWidth();
  const L = 15;
  const R = W - 15;
  const yTop = 18;

  // Bloque derecho: denominación del tipo, e-NCF, vencimiento y, en las notas, la referencia.
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...NAVY);
  doc.text(d.tipoLabel, R, yTop, { align: 'right', maxWidth: 85 });
  const tipoH = doc.getTextDimensions(d.tipoLabel, { maxWidth: 85, fontSize: 12 }).h;
  doc.setTextColor(0);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);

  let yr = yTop + tipoH + 3;
  const rRow = (label: string, val: string) => {
    if (!val) return;
    doc.text(`${label} ${val}`, R, yr, { align: 'right' });
    yr += 4.6;
  };
  rRow('e-NCF:', d.encf);
  rRow('Fecha Vencimiento:', d.fechaVencimiento);
  if (d.ncfModificado) {
    rRow('e-NCF Modificado:', d.ncfModificado);
    rRow('Código Modificación:', d.codigoModificacionLabel || d.codigoModificacion);
  }

  // Bloque izquierdo: emisor. La razón social va como identificación principal y tal cual está
  // registrada para el RNC; el nombre comercial, si lo hay, se añade aparte.
  const LW = 100;
  let y = yTop;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...NAVY);
  const rsLines = doc.splitTextToSize(d.emisor.razonSocial, LW) as string[];
  doc.text(rsLines, L, y);
  y += 5 * rsLines.length;

  doc.setTextColor(0);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const lRow = (val: string) => {
    if (!val) return;
    const w = doc.splitTextToSize(val, LW) as string[];
    doc.text(w, L, y);
    y += 4.2 * w.length;
  };
  if (d.emisor.nombreComercial && d.emisor.nombreComercial !== d.emisor.razonSocial) {
    lRow(`Nombre Comercial: ${d.emisor.nombreComercial}`);
  }
  if (d.emisor.sucursal) lRow(d.emisor.sucursal);
  lRow(`RNC ${d.emisor.rnc}`);
  const dir = [d.emisor.direccion, d.emisor.municipio, d.emisor.provincia]
    .filter(Boolean)
    .join(', ');
  if (dir) lRow(`Dirección: ${dir}`);
  doc.setFont('helvetica', 'bold');
  lRow(`Fecha Emisión: ${d.emisor.fechaEmision}`);
  doc.setFont('helvetica', 'normal');

  y = Math.max(y, yr) + 4;

  if (d.comprador.razonSocial || d.comprador.rnc) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    if (d.comprador.razonSocial) {
      doc.text(`Razón Social Cliente: ${d.comprador.razonSocial}`, L, y);
      y += 4.5;
    }
    if (d.comprador.rnc) {
      doc.text(`RNC Cliente: ${d.comprador.rnc}`, L, y);
      y += 4.5;
    }
    doc.setFont('helvetica', 'normal');
    y += 2;
  }

  // Detalle. La columna de descuento solo aparece si algún ítem lo trae, como en el modelo.
  const anyDesc = d.items.some((i) => i.descuento > 0);
  const head: string[] = ['Cantidad', 'Descripción', 'Unidad de Medida', 'Precio', 'ITBIS'];
  if (anyDesc) head.push('Descuento');
  head.push('Valor');

  const body = d.items.map((it) => {
    const row: string[] = [
      qty(it.cantidad),
      (it.exento ? 'E ' : '') + it.nombre,
      it.unidad || '',
      money(it.precio),
      money(it.itbis),
    ];
    if (anyDesc) row.push(it.descuento ? money(it.descuento) : '');
    row.push(money(it.valor));
    return row;
  });

  doc.autoTable({
    startY: y,
    head: [head],
    body,
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 1.6, lineColor: 190, lineWidth: 0.1, textColor: 20 },
    headStyles: { fillColor: [238, 238, 238], textColor: 20, fontStyle: 'bold', halign: 'center' },
    columnStyles: {
      0: { halign: 'right', cellWidth: 18 },
      1: { halign: 'left' },
      2: { halign: 'center', cellWidth: 24 },
    },
    margin: { left: L, right: 15 },
    // Precio, ITBIS, descuento y valor van alineados a la derecha, como cifras.
    didParseCell: (data: {
      section: string;
      column: { index: number };
      cell: { styles: { halign: string } };
    }) => {
      if (data.section === 'body' && data.column.index >= 3) data.cell.styles.halign = 'right';
    },
  });

  const finalY = (doc.lastAutoTable?.finalY ?? y) + 8;

  // Totales a la derecha, en cajas, como el modelo ilustrativo.
  let ty = finalY;
  const boxW = 34;
  const boxH = 6;
  const boxX = R - boxW;
  const tRow = (label: string, val: number, bold = false) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(9);
    doc.text(label, boxX - 2, ty + 4, { align: 'right' });
    doc.setDrawColor(150);
    doc.setLineWidth(0.2);
    doc.rect(boxX, ty, boxW, boxH);
    doc.text(money(val), R - 1.5, ty + 4, { align: 'right' });
    ty += boxH + 1.5;
  };
  if (d.totales.subtotalGravado) tRow('Subtotal Gravado:', d.totales.subtotalGravado);
  if (d.totales.subtotalExento) tRow('Subtotal Exento:', d.totales.subtotalExento);
  if (d.totales.totalIsc) tRow('Total ISC:', d.totales.totalIsc);
  if (d.totales.totalItbis) tRow('Total ITBIS:', d.totales.totalItbis);
  tRow('Total:', d.totales.total, true);

  await placeQrBlock(doc, d, Math.max(L, QR_MARGIN_MM), finalY);
  return doc;
}

// ─── Ticket de papel continuo (Factura de Consumo, tipo 32) ──────────────────

const TICKET_W = 80;

async function buildTicket(d: RiData): Promise<jsPDF> {
  const png = await qrPng(d.qrUrl, 300);
  // Dos pasadas: el alto depende de cuánto envuelva el texto, así que la primera mide sobre un
  // papel holgado y la segunda dibuja ya al tamaño exacto.
  const height = drawTicket(new jsPDF({ unit: 'mm', format: [TICKET_W, 600] }), d, png);
  const doc = new jsPDF({ unit: 'mm', format: [TICKET_W, height] });
  drawTicket(doc, d, png);
  return doc;
}

/** Dibuja el ticket y devuelve el alto ocupado en mm. */
function drawTicket(doc: jsPDF, d: RiData, qrImage: string): number {
  const width = TICKET_W;
  const L = 5;
  const R = width - 5;
  const CW = width - 10;
  let y = 8;
  doc.setFont('courier', 'normal');
  doc.setFontSize(8);

  // `y` avanza por CADA línea resultante del ajuste: si no, un texto largo (razón social,
  // dirección) se superpone con lo que viene detrás.
  const line = (txt: string, opts: { bold?: boolean; center?: boolean; size?: number } = {}) => {
    doc.setFont('courier', opts.bold ? 'bold' : 'normal');
    if (opts.size) doc.setFontSize(opts.size);
    const x = opts.center ? width / 2 : L;
    const wrapped = doc.splitTextToSize(txt, CW) as string[];
    const lh = opts.size ? opts.size * 0.42 : 3.4;
    doc.text(wrapped, x, y, { align: opts.center ? 'center' : 'left' });
    y += lh * wrapped.length;
    if (opts.size) doc.setFontSize(8);
  };
  const sep = () => {
    doc.setFont('courier', 'normal');
    doc.text('-'.repeat(42), L, y);
    y += 3.6;
  };

  line(d.emisor.razonSocial, { bold: true });
  if (d.emisor.nombreComercial && d.emisor.nombreComercial !== d.emisor.razonSocial) {
    line(`Nombre Comercial: ${d.emisor.nombreComercial}`);
  }
  if (d.emisor.sucursal) line(d.emisor.sucursal);
  if (d.emisor.direccion) line(`Dirección: ${d.emisor.direccion}`);
  line(`RNC: ${d.emisor.rnc}`);
  y += 1;
  sep();

  line(d.tipoLabel, { bold: true });
  line(`e-NCF: ${d.encf}`);
  if (d.comprador.rnc) line(`RNC Comprador: ${d.comprador.rnc}`);
  if (d.comprador.razonSocial) line(d.comprador.razonSocial);
  y += 1;
  sep();

  line(`Fecha Emisión: ${d.emisor.fechaEmision}`);
  sep();

  doc.setFont('courier', 'bold');
  doc.text('Cantidad', L, y);
  doc.text('Descripción', L + 22, y);
  doc.text('Unidad', R, y, { align: 'right' });
  y += 3.4;
  doc.text('Precio', L, y);
  doc.text('ITBIS', L + 22, y);
  doc.text('Valor', R, y, { align: 'right' });
  y += 3.4;
  doc.setFont('courier', 'normal');
  sep();

  for (const it of d.items) {
    doc.text(qty(it.cantidad), L, y);
    doc.text(it.nombre.slice(0, 18), L + 22, y);
    doc.text(it.unidad || '', R, y, { align: 'right' });
    y += 3.4;
    doc.text(money(it.precio), L, y);
    doc.text(money(it.itbis), L + 22, y);
    doc.text(money(it.valor), R, y, { align: 'right' });
    y += 3.8;
  }
  sep();

  const tRow = (label: string, val: number, bold = false) => {
    doc.setFont('courier', bold ? 'bold' : 'normal');
    doc.text(label, L + 14, y);
    doc.text(money(val), R, y, { align: 'right' });
    y += 3.6;
  };
  if (d.totales.subtotalGravado) tRow('Subtotal Gravado:', d.totales.subtotalGravado);
  if (d.totales.subtotalExento) tRow('Subtotal Exento:', d.totales.subtotalExento);
  if (d.totales.totalItbis) tRow('Total ITBIS:', d.totales.totalItbis);
  tRow('Total:', d.totales.total, true);
  y += 2;
  sep();

  // En el modelo de ticket el QR va centrado al final.
  doc.addImage(qrImage, 'PNG', (width - QR_MM) / 2, y, QR_MM, QR_MM);
  y += QR_MM + 3;
  doc.setFont('courier', 'bold');
  doc.setFontSize(7.5);
  doc.text(`Código de Seguridad: ${d.securityCode}`, width / 2, y, { align: 'center' });
  y += 3;
  doc.setFont('courier', 'normal');
  doc.text(`Fecha Firma: ${d.fechaFirma}`, width / 2, y, { align: 'center' });
  return y + 6;
}

/** QR inferior izquierdo, con el código de seguridad y la fecha de firma debajo. */
async function placeQrBlock(doc: jsPDF, d: RiData, x: number, y: number) {
  const png = await qrPng(d.qrUrl, 320);
  doc.addImage(png, 'PNG', x, y, QR_MM, QR_MM);
  let cy = y + QR_MM + 4;
  doc.setTextColor(0);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.text(`Código de Seguridad: ${d.securityCode}`, x, cy);
  cy += 4;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(`Fecha Firma: ${d.fechaFirma}`, x, cy);
}

/** PDF de la Representación Impresa, con el nombre de archivo que se sube al portal. */
export async function generateEcfRiPdf(d: RiData): Promise<{ blob: Blob; filename: string }> {
  const doc = d.docType === '32' ? await buildTicket(d) : await buildFullPage(d);
  return { blob: doc.output('blob'), filename: `RI_${d.docType}_${d.encf}.pdf` };
}

/** Guarda un PDF ya generado. Separado de la generación para no dibujarlo dos veces. */
export function descargarPdf(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
